import { Link } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  const handlePhoneClick = (e: React.MouseEvent, phoneNumber: string) => {
    if (!isMobile) {
      e.preventDefault();
      navigator.clipboard.writeText(phoneNumber);
      toast({
        title: "Phone number copied",
        description: `${phoneNumber} copied to clipboard`,
        duration: 3000
      });
    }
  };

  const openEmailCompose = (provider: 'gmail' | 'outlook' | 'yahoo' | 'apple' | 'copy') => {
    const email = 'info@fhsystemsltd.online';
    const subject = 'Consultation Request';
    const body = "Hello,\n\nI'm interested in learning more about your services.\n\nThank you";
    const urls = {
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      outlook: `https://outlook.live.com/mail/0/deeplink/compose?to=${email}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      yahoo: `https://compose.mail.yahoo.com/?to=${email}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      apple: `https://www.icloud.com/mail/`
    };
    if (provider === 'copy') {
      navigator.clipboard.writeText(email);
      toast({
        title: "Email copied",
        description: `${email} copied to clipboard`,
        duration: 3000
      });
    } else {
      window.open(urls[provider], '_blank');
    }
  };

  const footerLinks = {
    company: [{
      label: "About Us",
      path: "/about"
    }, {
      label: "Services",
      path: "/services"
    }, {
      label: "FAQ",
      path: "/faq"
    }, {
      label: "Pricing",
      path: "/pricing"
    }],
    resources: [{
      label: "How It Works",
      path: "/how-it-works"
    }, {
      label: "Contact",
      path: "/contact"
    }, {
      label: "Terms & Conditions",
      path: "/terms-and-conditions"
    }, {
      label: "Privacy Policy",
      path: "/privacy"
    }, {
      label: "Referral Terms",
      path: "/referral-terms"
    }, {
      label: "Cookie Policy",
      path: "/cookie-policy"
    }],
    admin: [{
      label: "Sign In",
      path: "/auth"
    }]
  };

  const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent('openCookiePreferences'));
  };

  return (
    <footer className="relative bg-gradient-to-b from-deep-navy via-black to-deep-navy border-t border-neon-glow/10">
      {/* Subtle Background Pattern - disabled on mobile */}
      {!isMobile && (
        <div className="absolute inset-0 hexagon-bg opacity-10 pointer-events-none" />
      )}
      
      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-12 mb-8 md:mb-12">
          {/* Logo & Description - Full width on mobile */}
          <div className="col-span-2 lg:col-span-1 space-y-4 md:space-y-6">
            <Link to="/" className="flex items-center space-x-2 mb-4 hover:opacity-80 transition-opacity">
              <img alt="F&H Systems" width={40} height={40} className="h-8 md:h-10 w-auto" src="/lovable-uploads/d733ced6-b567-42aa-a3b4-934827e192b1.png" />
            </Link>
            <p className="text-xs md:text-sm text-gray-400 leading-relaxed">
              Building custom web apps and digital systems that give you clarity and control over
              your business operations.
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-display font-bold text-white mb-3 md:mb-6 uppercase tracking-wider text-xs md:text-sm">Company</h3>
            <ul className="space-y-2 md:space-y-3">
              {footerLinks.company.map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-xs md:text-sm text-gray-400 hover:text-neon-glow transition-colors duration-200 inline-flex items-center group">
                    <span className="w-0 group-hover:w-2 h-px bg-neon-glow transition-all duration-200 mr-0 group-hover:mr-2" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="font-display font-bold text-white mb-3 md:mb-6 uppercase tracking-wider text-xs md:text-sm">Resources</h3>
            <ul className="space-y-2 md:space-y-3">
              {footerLinks.resources.map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-xs md:text-sm text-gray-400 hover:text-neon-glow transition-colors duration-200 inline-flex items-center group">
                    <span className="w-0 group-hover:w-2 h-px bg-neon-glow transition-all duration-200 mr-0 group-hover:mr-2" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Full width on mobile */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="font-display font-bold text-white mb-3 md:mb-6 uppercase tracking-wider text-xs md:text-sm">Get In Touch</h3>
            <ul className="space-y-3 md:space-y-4">
              <li className="flex items-start space-x-3 text-xs md:text-sm text-gray-400 group">
                <Mail className="h-4 w-4 md:h-5 md:w-5 mt-0.5 text-neon-glow/60 group-hover:text-neon-glow transition-colors shrink-0" />
                {isMobile ? (
                  <a href="mailto:info@fhsystemsltd.online?subject=Consultation Request&body=Hello,%0D%0A%0D%0AI'm interested in learning more about your services.%0D%0A%0D%0AThank you" className="hover:text-neon-glow transition-colors duration-200 break-all">
                    info@fhsystemsltd.online
                  </a>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="hover:text-neon-glow transition-colors duration-200 cursor-pointer text-left">
                        info@fhsystemsltd.online
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2">
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" className="justify-start" onClick={() => openEmailCompose('gmail')}>
                          Gmail
                        </Button>
                        <Button variant="ghost" className="justify-start" onClick={() => openEmailCompose('outlook')}>
                          Outlook
                        </Button>
                        <Button variant="ghost" className="justify-start" onClick={() => openEmailCompose('yahoo')}>
                          Yahoo
                        </Button>
                        <Button variant="ghost" className="justify-start" onClick={() => openEmailCompose('apple')}>
                          iCloud Mail
                        </Button>
                        <Button variant="ghost" className="justify-start" onClick={() => openEmailCompose('copy')}>
                          Copy Address
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </li>
              <li className="flex items-start space-x-3 text-xs md:text-sm text-gray-400 group">
                <Phone className="h-4 w-4 md:h-5 md:w-5 mt-0.5 text-neon-glow/60 group-hover:text-neon-glow transition-colors shrink-0" />
                <a href="tel:+447428591835" onClick={e => handlePhoneClick(e, '+44 7428 591835')} className="hover:text-neon-glow transition-colors duration-200 cursor-pointer">
                  +44 7428 591835
                </a>
              </li>
              {footerLinks.admin.map(link => (
                <li key={link.path}>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neon-glow/10 pt-6 md:pt-8 flex flex-col items-center gap-4">
          <div className="flex flex-col md:flex-row justify-between items-center w-full gap-3 md:gap-4">
            <p className="text-xs md:text-sm text-gray-500 text-center">
              © {currentYear} F&H Systems. All rights reserved.
            </p>
            <button onClick={openCookieSettings} className="text-xs text-gray-500 hover:text-neon-glow transition-colors cursor-pointer">
              Cookie Settings
            </button>
            <p className="text-[10px] md:text-xs text-gray-600 uppercase tracking-wide text-center">
              Built By  <span className="text-neon-glow">●</span> F&amp;H Systems LTD  
            </p>
          </div>
          
          {/* Company Registration Details */}
          <div className="text-[10px] md:text-xs text-gray-600 text-center space-y-1">
            <p>F&H Systems Ltd | Company No. 16811367 | Registered in England and Wales</p>
            <p>Registered Office: 40 Aldridge Avenue, Edgware, England, HA8 8TA</p>
          </div>
        </div>
      </div>

      {/* Subtle Bottom Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-px bg-gradient-to-r from-transparent via-neon-glow/30 to-transparent" />
    </footer>
  );
};